import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageContentShell } from "../../components/layout/PageContentShell";
import { PageHeader } from "../../components/Page/PageHeader";
import {
  DEPLOY_TARGETS,
  productionDeploymentService,
  type DeploymentRun,
  type DeploymentStep,
  type DeployTarget,
  type OpenPullRequest,
  type PullRequestAction,
  type RunStatus,
  type StepStatus,
} from "../../services/productionDeploymentService";
import { apiMessage } from "../../utils/apiError";

const stepColor = (status: StepStatus): "default" | "info" | "success" | "error" | "warning" => {
  switch (status) {
    case "RUNNING":
      return "info";
    case "SUCCESS":
      return "success";
    case "FAILED":
      return "error";
    case "SKIPPED":
      return "warning";
    default:
      return "default";
  }
};

const runColor = (status: RunStatus): "info" | "success" | "error" => {
  if (status === "SUCCESS") return "success";
  if (status === "FAILED") return "error";
  return "info";
};

function formatWhen(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function canMerge(pr: OpenPullRequest) {
  return !pr.draft && pr.mergeable !== false;
}

function canDelete(pr: OpenPullRequest) {
  return !pr.fromFork && !pr.protectedHeadBranch;
}

function StepRow({ step }: { step: DeploymentStep }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        p: 1.5,
        border: 1,
        borderColor: step.status === "FAILED" ? "error.light" : "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">{step.label}</Typography>
        <Chip size="small" color={stepColor(step.status)} label={t(`deployment.stepStatus.${step.status}`)} />
      </Stack>
      {step.status === "RUNNING" ? <LinearProgress sx={{ mt: 1 }} /> : null}
      {step.detail ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {step.detail}
        </Typography>
      ) : null}
      {step.error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {step.error}
        </Alert>
      ) : null}
      {step.stages && step.stages.length > 0 ? (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
          {step.stages.map((stage) => (
            <Chip key={stage.name} size="small" variant="outlined" label={`${stage.name}: ${stage.status}`} />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

function mergeableLabel(pr: OpenPullRequest, t: (key: string) => string) {
  if (pr.draft) return t("deployment.draft");
  if (pr.mergeable === false) return t("deployment.conflict");
  if (pr.mergeable === true) return t("deployment.ready");
  return t("deployment.checking");
}

function mergeableColor(pr: OpenPullRequest): "default" | "success" | "error" | "warning" {
  if (pr.draft) return "warning";
  if (pr.mergeable === false) return "error";
  if (pr.mergeable === true) return "success";
  return "default";
}

const compactBtnSx = { whiteSpace: "nowrap", minWidth: "auto", px: 1 } as const;

function PrActionButtons({
  disabled,
  mergeDisabled,
  deleteDisabled,
  onAction,
}: {
  disabled: boolean;
  mergeDisabled: boolean;
  deleteDisabled: boolean;
  onAction: (action: PullRequestAction) => void;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={0.5} flexShrink={0} sx={{ whiteSpace: "nowrap" }}>
      <Button size="small" sx={compactBtnSx} disabled={disabled || mergeDisabled} onClick={() => onAction("MERGE")}>
        {t("deployment.merge")}
      </Button>
      <Button size="small" sx={compactBtnSx} disabled={disabled} onClick={() => onAction("CLOSE")}>
        {t("deployment.close")}
      </Button>
      <Button size="small" color="warning" sx={compactBtnSx} disabled={disabled} onClick={() => onAction("REJECT")}>
        {t("deployment.reject")}
      </Button>
      <Button size="small" color="error" sx={compactBtnSx} disabled={disabled || deleteDisabled} onClick={() => onAction("DELETE")}>
        {t("deployment.delete")}
      </Button>
    </Stack>
  );
}

export function DeploymentPage() {
  const { t } = useTranslation();
  const [runs, setRuns] = useState<DeploymentRun[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<DeploymentRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState<DeployTarget[]>([]);
  const [triggering, setTriggering] = useState(false);
  const [prs, setPrs] = useState<OpenPullRequest[]>([]);
  const [prsLoading, setPrsLoading] = useState(true);
  const [prsError, setPrsError] = useState<string | null>(null);
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [pendingAction, setPendingAction] = useState<PullRequestAction | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [applying, setApplying] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await productionDeploymentService.listRuns();
      setRuns(items);
      setSelectedId((current) => current || items[0]?.id || null);
    } catch (e) {
      setError(apiMessage(e) || t("deployment.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadPrs = useCallback(async () => {
    setPrsLoading(true);
    setPrsError(null);
    try {
      const items = await productionDeploymentService.listOpenPullRequests();
      setPrs(items);
      setSelectedNumbers((current) => current.filter((number) => items.some((pr) => pr.number === number)));
    } catch (e) {
      setPrsError(apiMessage(e) || t("deployment.prsLoadError"));
    } finally {
      setPrsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadList();
    void loadPrs();
  }, [loadList, loadPrs]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        const run = await productionDeploymentService.getRun(selectedId);
        if (!cancelled) setSelected(run);
        return run;
      } catch (e) {
        if (!cancelled) setError(apiMessage(e) || t("deployment.loadError"));
        return null;
      }
    };
    void refresh();
    const timer = window.setInterval(async () => {
      const run = await refresh();
      if (run && run.status !== "RUNNING") {
        void loadList();
      }
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [selectedId, loadList, t]);

  const busy = selected?.status === "RUNNING" || triggering || applying || runs.some((run) => run.status === "RUNNING");
  const selectedPrs = useMemo(
    () => prs.filter((pr) => selectedNumbers.includes(pr.number)),
    [prs, selectedNumbers]
  );
  const allSelected = prs.length > 0 && selectedNumbers.length === prs.length;

  const toggleNumber = (number: number) => {
    setSelectedNumbers((current) =>
      current.includes(number) ? current.filter((item) => item !== number) : [...current, number]
    );
  };

  const toggleAll = () => {
    setSelectedNumbers(allSelected ? [] : prs.map((pr) => pr.number));
  };

  const onTrigger = async () => {
    if (selectedTargets.length === 0) {
      setError(t("deployment.targetRequired"));
      return;
    }
    setTriggering(true);
    setError(null);
    try {
      const run = await productionDeploymentService.trigger(selectedTargets);
      setConfirmOpen(false);
      setSelectedId(run.id);
      setSelected(run);
      await loadList();
    } catch (e) {
      setError(apiMessage(e) || t("deployment.triggerError"));
    } finally {
      setTriggering(false);
    }
  };

  const openAction = (action: PullRequestAction, numbers?: number[]) => {
    const chosen = numbers ?? selectedNumbers;
    if (chosen.length === 0) return;
    setSelectedNumbers(chosen);
    setRejectReason("");
    setPendingAction(action);
  };

  const applyAction = async () => {
    if (!pendingAction || selectedNumbers.length === 0) return;
    if (pendingAction === "REJECT" && !rejectReason.trim()) {
      setPrsError(t("deployment.rejectReasonRequired"));
      return;
    }
    setApplying(true);
    setPrsError(null);
    setError(null);
    try {
      const results = await productionDeploymentService.applyPullRequestActions({
        numbers: selectedNumbers,
        action: pendingAction,
        reason: pendingAction === "REJECT" ? rejectReason.trim() : undefined,
      });
      const failed = results.filter((result) => !result.success);
      if (failed.length > 0) {
        setPrsError(failed.map((result) => result.error || `#${result.number}`).join(" · "));
      }
      setPendingAction(null);
      setRejectReason("");
      await loadPrs();
    } catch (e) {
      setPrsError(apiMessage(e) || t("deployment.prsActionError"));
    } finally {
      setApplying(false);
    }
  };

  const actionTitle =
    pendingAction === "MERGE"
      ? t("deployment.mergeTitle")
      : pendingAction === "CLOSE"
        ? t("deployment.closeTitle")
        : pendingAction === "REJECT"
          ? t("deployment.rejectTitle")
          : pendingAction === "DELETE"
            ? t("deployment.deleteTitle")
            : "";
  const actionBody =
    pendingAction === "MERGE"
      ? t("deployment.mergeBody")
      : pendingAction === "CLOSE"
        ? t("deployment.closeBody")
        : pendingAction === "REJECT"
          ? t("deployment.rejectBody")
          : pendingAction === "DELETE"
            ? t("deployment.deleteBody")
            : "";

  return (
    <PageContentShell>
      <PageHeader
        title={t("deployment.title")}
        subtitle={t("deployment.description")}
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => { void loadList(); void loadPrs(); }} disabled={loading || prsLoading}>
              {t("deployment.refresh")}
            </Button>
            <Button
              variant="contained"
              startIcon={<RocketLaunchOutlinedIcon />}
              onClick={() => {
                setSelectedTargets([]);
                setConfirmOpen(true);
              }}
              disabled={busy}
            >
              {t("deployment.deploy")}
            </Button>
          </Stack>
        }
      />
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Box sx={{ mb: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
          <Box sx={{ minWidth: 0, flex: "1 1 0", maxWidth: { sm: "42%" } }}>
            <Typography variant="subtitle1">{t("deployment.prsTitle")}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap title={t("deployment.prsSubtitle")}>
              {t("deployment.prsSubtitle")}
            </Typography>
          </Box>
          <PrActionButtons
            disabled={busy || selectedNumbers.length === 0}
            mergeDisabled={selectedPrs.some((pr) => !canMerge(pr))}
            deleteDisabled={selectedPrs.some((pr) => !canDelete(pr))}
            onAction={(action) => openAction(action)}
          />
        </Stack>
        {prsError ? <Alert severity="error" sx={{ mb: 1.5 }}>{prsError}</Alert> : null}
        {prsLoading && prs.length === 0 ? (
          <CircularProgress size={24} />
        ) : prs.length === 0 ? (
          <Typography color="text.secondary">{t("deployment.prsEmpty")}</Typography>
        ) : (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Checkbox size="small" checked={allSelected} indeterminate={selectedNumbers.length > 0 && !allSelected} onChange={toggleAll} disabled={busy} />
              <Typography variant="caption" color="text.secondary">
                {t("deployment.selected", { count: selectedNumbers.length })}
              </Typography>
            </Stack>
            {prs.map((pr) => (
              <Box
                key={pr.number}
                sx={{
                  p: 1.25,
                  border: 1,
                  borderColor: selectedNumbers.includes(pr.number) ? "primary.main" : "divider",
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <Checkbox
                      size="small"
                      checked={selectedNumbers.includes(pr.number)}
                      onChange={() => toggleNumber(pr.number)}
                      disabled={busy}
                    />
                    <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="subtitle2" noWrap title={`#${pr.number} ${pr.title}`}>
                          #{pr.number} {pr.title}
                        </Typography>
                        {pr.url ? (
                          <Link href={pr.url} target="_blank" rel="noreferrer" variant="caption" underline="hover" sx={{ flexShrink: 0 }}>
                            {t("deployment.openOnGitHub")} <OpenInNewIcon sx={{ fontSize: 12, ml: 0.25 }} />
                          </Link>
                        ) : null}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {pr.author || "—"} · {pr.headBranch} → {pr.baseBranch}
                        {pr.updatedAt ? ` · ${formatWhen(pr.updatedAt)}` : ""}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, overflow: "hidden" }}>
                        <Chip size="small" color={mergeableColor(pr)} label={mergeableLabel(pr, t)} />
                        {pr.fromFork ? <Chip size="small" variant="outlined" label={t("deployment.fork")} /> : null}
                        {pr.protectedHeadBranch ? (
                          <Chip size="small" variant="outlined" label={t("deployment.protectedBranch")} />
                        ) : null}
                      </Stack>
                    </Box>
                  </Stack>
                  <PrActionButtons
                    disabled={busy}
                    mergeDisabled={!canMerge(pr)}
                    deleteDisabled={!canDelete(pr)}
                    onAction={(action) => openAction(action, [pr.number])}
                  />
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "3fr 1fr" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.25 }}>{t("deployment.historyTitle")}</Typography>
          {loading && runs.length === 0 ? (
            <CircularProgress size={28} />
          ) : runs.length === 0 ? (
            <Typography color="text.secondary">{t("deployment.empty")}</Typography>
          ) : (
            <Stack spacing={2}>
              {runs.map((run) => (
                <Box
                  key={run.id}
                  onClick={() => setSelectedId(run.id)}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: selectedId === run.id ? "primary.main" : "divider",
                    borderRadius: 1,
                    cursor: "pointer",
                    bgcolor: selectedId === run.id ? "action.hover" : "background.paper",
                  }}
                >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatWhen(run.createdAt)} · {run.triggeredBy || "—"}
                </Typography>
                <Chip size="small" color={runColor(run.status)} label={t(`deployment.runStatus.${run.status}`)} />
              </Stack>
              {run.targets && run.targets.length > 0 ? (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                  {run.targets.map((target) => (
                    <Chip key={target} size="small" variant="outlined" label={t(`deployment.targets.${target}`)} />
                  ))}
                </Stack>
              ) : null}
                  <Typography variant="caption" color="text.secondary">
                    {run.sourceBranch} → {run.targetBranch}
                    {run.mergeSha ? ` · ${run.mergeSha.slice(0, 7)}` : ""}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.25 }}>{t("deployment.stepsTitle")}</Typography>
          {selected ? (
            <Stack spacing={1.25}>
              {selected.error ? <Alert severity="error">{selected.error}</Alert> : null}
              {selected.steps?.map((step) => (
                <StepRow key={step.key} step={step} />
              ))}
            </Stack>
          ) : loading ? (
            <CircularProgress size={28} />
          ) : null}
        </Box>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("deployment.confirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t("deployment.confirmBody")}</Typography>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>{t("deployment.targetsTitle")}</Typography>
          <FormGroup>
            {DEPLOY_TARGETS.map((target) => (
              <FormControlLabel
                key={target}
                control={
                  <Checkbox
                    checked={selectedTargets.includes(target)}
                    onChange={() =>
                      setSelectedTargets((current) =>
                        current.includes(target)
                          ? current.filter((item) => item !== target)
                          : [...current, target]
                      )
                    }
                  />
                }
                label={t(`deployment.targets.${target}`)}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t("deployment.cancel")}</Button>
          <Button
            variant="contained"
            onClick={() => void onTrigger()}
            disabled={triggering || selectedTargets.length === 0}
          >
            {triggering ? t("deployment.starting") : t("deployment.deploy")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingAction !== null} onClose={() => setPendingAction(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{actionTitle}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: pendingAction === "REJECT" ? 2 : 0 }}>{actionBody}</Typography>
          {pendingAction === "REJECT" ? (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              label={t("deployment.rejectReason")}
              value={rejectReason}
              onChange={(event) => setRejectReason(event.target.value)}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)} disabled={applying}>{t("deployment.cancel")}</Button>
          <Button
            variant="contained"
            color={pendingAction === "DELETE" ? "error" : pendingAction === "REJECT" ? "warning" : "primary"}
            onClick={() => void applyAction()}
            disabled={applying || (pendingAction === "REJECT" && !rejectReason.trim())}
          >
            {applying
              ? t("deployment.applying")
              : pendingAction === "MERGE"
                ? t("deployment.merge")
                : pendingAction === "CLOSE"
                  ? t("deployment.close")
                  : pendingAction === "REJECT"
                    ? t("deployment.reject")
                    : t("deployment.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContentShell>
  );
}
